import React from 'react';
import {
  Box, Button, Chip, Divider, List, ListItem, ListItemIcon, ListItemText,
  Paper, Typography,
} from '@mui/material';
import { CheckCircle, WarningAmber } from '@mui/icons-material';
import { SERVICE_OPTIONS, sectionCompletion } from '../onboardingMapping.js';
import { provinceName } from '../geography.js';

const SECTIONS = [
  { key: 'company', label: 'Company', step: 0 },
  { key: 'services', label: 'Services', step: 1 },
  { key: 'serviceArea', label: 'Service area', step: 2 },
  { key: 'crews', label: 'Crews & equipment', step: 3 },
  { key: 'pricing', label: 'Pricing', step: 4 },
  { key: 'availability', label: 'Availability', step: 5 },
  { key: 'documents', label: 'Documents', step: 6 },
];

export default function ReviewStep({ answers, pricingStatus, onGoToStep }) {
  const completion = sectionCompletion(answers);
  const sa = answers.serviceArea;
  const activeServices = SERVICE_OPTIONS.filter(s => answers.services[s.id]);

  return (
    <>
      <Typography variant="h5" fontWeight={600} gutterBottom>Review your MOVD profile</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Make sure everything looks right, then submit for review. Approval usually takes 1–2 business days.
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <List disablePadding>
          {SECTIONS.map((section, index) => {
            const done = completion[section.key];
            return (
              <React.Fragment key={section.key}>
                {index > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    !done && (
                      <Button size="small" variant="outlined" onClick={() => onGoToStep(section.step)}>
                        Fix this
                      </Button>
                    )
                  }
                >
                  <ListItemIcon>
                    {done ? <CheckCircle color="success" /> : <WarningAmber color="warning" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={section.label}
                    secondary={done ? 'Complete' : 'Incomplete — required before you can receive bookings'}
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>SUMMARY</Typography>
        <SummaryRow label="Company" value={answers.company.companyName || '—'} />
        <SummaryRow label="Services" value={activeServices.length ? activeServices.map(s => s.label).join(', ') : 'None selected'} />
        <SummaryRow
          label="Service area"
          value={sa.homeCity
            ? `${sa.homeCity}${sa.localCities.length ? `, ${sa.localCities.join(', ')}` : ''}, ${provinceName(sa.homeProvince)}${answers.services.longDistance && sa.destinationProvinces.length ? ` + long-distance to ${sa.destinationProvinces.map(provinceName).join(', ')}` : ''}`
            : '—'}
        />
        <SummaryRow label="Crew sizes" value={answers.crews.crewSizes.length ? answers.crews.crewSizes.map(s => (s === 5 ? '5+' : s)).join(', ') : '—'} />
        <SummaryRow
          label="Pricing"
          value={pricingStatus?.valid ? 'Configured' : 'Needs attention'}
        />
        <SummaryRow label="Documents" value={`${answers.documents.length} file${answers.documents.length === 1 ? '' : 's'} ready to upload`} />
      </Paper>

      {pricingStatus && !pricingStatus.valid && (
        <Box sx={{ mb: 2 }}>
          <Chip
            color="warning" variant="outlined"
            label={`Pricing incomplete${pricingStatus.missingFields?.length ? `: missing ${pricingStatus.missingFields.join(', ')}` : ''}`}
          />
        </Box>
      )}
    </>
  );
}

function SummaryRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  );
}
